/**
 * `<PageHead>` — the shared per-surface page header.
 *
 * U6 (surface sweeps): renders the CANONICAL house header markup — the same
 * `.page-head > .page-head__main > .page-head__eyebrow? + h1.page-title +
 * .page-sub?` + `.page-head__actions` structure that Chat / Settings /
 * NotePoolMetrics hand-roll — so surfaces can adopt the component without a
 * visual delta. All styling comes from app.css (`.page-head` family); the
 * previous Tailwind utility classes were inert (tailwind.css is not imported
 * anywhere) and have been dropped.
 *
 * Designed to be the FIRST child inside `.main`. Pairs with `<PageBody>` for
 * the scrollable region below.
 */

import type { ReactNode } from 'react';

interface PageHeadProps {
  title: ReactNode;
  /** Small uppercase mono label rendered above the title
   *  (`.page-head__eyebrow` — names the surface category, e.g. "retrieval"). */
  eyebrow?: ReactNode;
  /** Subtitle rendered under the title (`.page-sub`). */
  sub?: ReactNode;
  /** Right-aligned actions cluster (`.page-head__actions`). */
  children?: ReactNode;
}

export function PageHead({ title, eyebrow, sub, children }: PageHeadProps): JSX.Element {
  return (
    <div className="page-head">
      <div className="page-head__main">
        {eyebrow !== undefined && <div className="page-head__eyebrow">{eyebrow}</div>}
        <h1 className="page-title">{title}</h1>
        {sub !== undefined && sub !== null && <p className="page-sub">{sub}</p>}
      </div>
      {children !== undefined && children !== null && (
        <div className="page-head__actions">{children}</div>
      )}
    </div>
  );
}
